// src/stores/propsStore.ts
import { create } from 'zustand';
import { BasePick, Side, Sport, uid } from '../domain/types';
import { useAuthStore } from './authStore';

interface PropsState {
  // props unificadas NBA + NHL
  allProps: BasePick[];
  nbaProps: BasePick[];
  nhlProps: BasePick[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // ações
  setProps: (props: BasePick[]) => void;
  setNbaProps: (props: BasePick[]) => void;
  setNhlProps: (props: BasePick[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // fetch do backend
  fetchAll: () => Promise<void>;
  fetchBySport: (sport: Sport) => Promise<void>;

  // filtros
  getPropsByGame: (game: string) => BasePick[];
  getBestByGame: (game: string, limit?: number) => BasePick[];
  getAllGames: () => string[];
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');

function authHeaders(): HeadersInit {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

type ApiHitSide = { hits?: number; total?: number; pct?: number };
type ApiProp = Partial<BasePick> & {
  player?: string;
  market?: string;
  label?: string;
  stat?: string;
  statKey?: string;
  line?: number;
  game?: string;
  start?: string;
  startISO?: string;
  price?: number | null;
  values?: number[];
  dates?: string[];
  hitRate?: { over?: ApiHitSide; under?: ApiHitSide };
  bestSide?: string;
  bestPct?: number;
  pct?: number;
};

function decimalOdds(prop: ApiProp): number {
  if (typeof prop.odds === 'number' && Number.isFinite(prop.odds) && prop.odds > 1) return prop.odds;
  const price = prop.price;
  if (typeof price !== 'number' || !Number.isFinite(price) || price === 0) return 1.91;
  return price > 0 ? 1 + price / 100 : 1 + 100 / Math.abs(price);
}

function normalizeProps(payload: unknown, sport: Sport): BasePick[] {
  const rows: ApiProp[] = Array.isArray(payload)
    ? payload as ApiProp[]
    : ((payload as { props?: ApiProp[] } | null)?.props ?? []);

  return rows.flatMap((prop) => {
    if (!prop.player || typeof prop.line !== 'number' || !prop.game) return [];
    const values = prop.values ?? [];
    const over = prop.hitRate?.over?.pct;
    const under = prop.hitRate?.under?.pct;
    const side: Side = prop.side === Side.UNDER || prop.bestSide === Side.UNDER
      ? Side.UNDER
      : prop.side === Side.OVER || prop.bestSide === Side.OVER
        ? Side.OVER
        : (over ?? -1) >= (under ?? -1) ? Side.OVER : Side.UNDER;
    const computed = values.slice(0, 20).filter(value => value !== prop.line);
    const calculatedPct = computed.length
      ? Math.round(computed.filter(value => side === 'over' ? value > prop.line! : value < prop.line!).length * 100 / computed.length)
      : undefined;
    const hitRatePct = prop.hitRatePct ?? prop.bestPct ?? prop.pct ?? (side === 'over' ? over : under) ?? calculatedPct;
    const startISO = prop.startISO ?? prop.start ?? '';
    const market = prop.label ?? prop.market ?? 'Prop';

    return [{
      id: prop.id ?? uid(),
      sport,
      player: prop.player,
      market,
      statKey: prop.statKey ?? prop.stat ?? prop.market ?? market,
      line: prop.line,
      side,
      odds: decimalOdds(prop),
      game: prop.game,
      startISO,
      hitRatePct,
      overHitRatePct: over,
      underHitRatePct: under,
      overHits: prop.hitRate?.over?.hits,
      overSample: prop.hitRate?.over?.total,
      underHits: prop.hitRate?.under?.hits,
      underSample: prop.hitRate?.under?.total,
      historyValues: values,
      historyDates: prop.dates ?? [],
    }];
  });
}

export const usePropsStore = create<PropsState>((set, get) => ({
  allProps: [],
  nbaProps: [],
  nhlProps: [],
  isLoading: false,
  error: null,
  lastFetched: null,

  setProps: (props) => set({ allProps: props, lastFetched: Date.now() }),
  setNbaProps: (props) => set({ nbaProps: props }),
  setNhlProps: (props) => set({ nhlProps: props }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  fetchAll: async () => {
    set({ isLoading: true, error: null });
    try {
      const [nbaRes, nhlRes] = await Promise.all([
        fetch(`${API_BASE}/props?sport=NBA`, { headers: authHeaders() }),
        fetch(`${API_BASE}/props?sport=NHL`, { headers: authHeaders() }),
      ]);
      if (!nbaRes.ok || !nhlRes.ok) throw new Error('Falha ao buscar props');
      const nba = normalizeProps(await nbaRes.json(), Sport.NBA);
      const nhl = normalizeProps(await nhlRes.json(), Sport.NHL);
      set({
        nbaProps: nba,
        nhlProps: nhl,
        allProps: [...nba, ...nhl],
        lastFetched: Date.now(),
        isLoading: false,
      });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  fetchBySport: async (sport: Sport) => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_BASE}/props?sport=${sport}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Falha ao buscar ${sport}`);
      const data = normalizeProps(await res.json(), sport);
      if (sport === 'NBA') set({ nbaProps: data });
      else set({ nhlProps: data });
      set({ allProps: [...get().nbaProps, ...get().nhlProps], lastFetched: Date.now(), isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  getPropsByGame: (game) => get().allProps.filter(p => p.game === game),

  getBestByGame: (game, limit = 4) =>
    get().allProps
      .filter(p => p.game === game && typeof p.hitRatePct === 'number')
      .sort((a, b) => (b.hitRatePct ?? 0) - (a.hitRatePct ?? 0))
      .slice(0, limit),

  getAllGames: () => Array.from(new Set(get().allProps.map(p => p.game))).sort(),
}));
